import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../../components/ui/sheet';
import { MapPin, Briefcase, Sparkles, Building2, Trash2, Bookmark, Share2 } from 'lucide-react';
import { useSavedSearches, SavedSearch, NotificationCadence } from '../../hooks/useSavedSearches';
import { useJobsQuery } from '../../hooks/useJobsQuery';
import posthog from 'posthog-js';
import { Helmet } from 'react-helmet-async';
import { isEmployerVerified } from '../../lib/jobVerification';
import { useOccupationSearch } from '../../hooks/useOccupations';

const jobTypes = ['Full-time', 'Part-time', 'Contract', 'Temporary'] as const;

const sortOptions = [
  { value: 'newest', label: 'Newest' },
  { value: 'comp', label: 'Highest compensation' },
] as const;

const tagOptions = [
  { value: 'returner friendly', label: 'Returner programme' },
  { value: 'flexible hours', label: 'Flexible hours' },
  { value: 'hybrid', label: 'Hybrid' },
  { value: '4-day week', label: '4-day week' },
  { value: 'leadership', label: 'Leadership' },
  { value: 'mentorship', label: 'Mentorship' },
  { value: 'ai', label: 'AI / Data' },
] as const;

const cadenceOptions: { value: NotificationCadence; label: string; description: string }[] = [
  { value: 'daily', label: 'Daily', description: 'Morning digest of new matches' },
  { value: 'weekly', label: 'Weekly', description: 'Monday summary of new roles' },
  { value: 'off', label: 'Off', description: 'Pause email alerts' },
];

type SortValue = (typeof sortOptions)[number]['value'];

const formatCurrency = (value?: number | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: 0,
  }).format(value);
};

const normalisePayInput = (value: string) => {
  const numeric = Number.parseInt(value.replace(/[^\d]/g, ''), 10);
  return Number.isNaN(numeric) ? null : numeric;
};

const buildPaySummary = (minValue: number | null, maxValue: number | null) => {
  const minLabel = formatCurrency(minValue);
  const maxLabel = formatCurrency(maxValue);
  if (minLabel && maxLabel) return `${minLabel} – ${maxLabel}`;
  if (minLabel) return `${minLabel}+`;
  if (maxLabel) return `Up to ${maxLabel}`;
  return '';
};

export function JobsIndex() {
  const [query, setQuery] = useState('');
  const [location, setLocation] = useState('');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [returnerFriendly, setReturnerFriendly] = useState(false);
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [sort, setSort] = useState<SortValue>('newest');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [minPayInput, setMinPayInput] = useState('');
  const [maxPayInput, setMaxPayInput] = useState('');
  const occupationSearch = useOccupationSearch(query);

  const {
    savedSearches,
    addSearch,
    removeSearch,
    updateCadence,
    defaultCadence,
    setDefaultCadence,
    savedSearchesLoading,
    savedSearchesError,
    mostRecent,
    settingsLoading,
    settingsError,
    shareSavedSearch,
  } = useSavedSearches();
  const [cadence, setCadence] = useState<NotificationCadence>(defaultCadence);
  const [savedSearchSheetOpen, setSavedSearchSheetOpen] = useState(false);
  const queryBlurRef = useRef('');
  const locationBlurRef = useRef('');
  const siteUrl = import.meta.env.VITE_SITE_URL?.replace(/\/$/, '');

  useEffect(() => {
    setCadence(defaultCadence);
  }, [defaultCadence]);

  const trackFilterChange = (filter: string, value: unknown) => {
    posthog.capture('jobs_filter_changed', {
      filter,
      value,
      savedSearchCount: savedSearches.length,
    });
  };

  const handleQueryChange = (value: string) => {
    setQuery(value);
  };

  const handleQueryBlur = () => {
    const trimmed = query.trim();
    if (queryBlurRef.current === trimmed) return;
    queryBlurRef.current = trimmed;
    trackFilterChange('query', trimmed || null);
  };

  const handleLocationChange = (value: string) => {
    setLocation(value);
  };

  const handleLocationBlur = () => {
    const trimmed = location.trim();
    if (locationBlurRef.current === trimmed) return;
    locationBlurRef.current = trimmed;
    trackFilterChange('location', trimmed || null);
  };

  const handleJobTypeChange = (value: string) => {
    setSelectedType(value);
    trackFilterChange('jobType', value);
  };

  const handleReturnerFriendlyChange = (checked: boolean | 'indeterminate') => {
    const nextValue = checked === true;
    setReturnerFriendly(nextValue);
    trackFilterChange('returnerFriendly', nextValue);
  };

  const handleRemoteOnlyChange = (checked: boolean | 'indeterminate') => {
    const nextValue = checked === true;
    setRemoteOnly(nextValue);
    trackFilterChange('remoteOnly', nextValue);
  };

  const handleSortChange = (value: SortValue) => {
    setSort(value);
    trackFilterChange('sort', value);
  };

  const toggleTag = (tagValue: string) => {
    setSelectedTags((previous) => {
      const exists = previous.includes(tagValue);
      const next = exists ? previous.filter((tag) => tag !== tagValue) : [...previous, tagValue];
      trackFilterChange('tags', next);
      return next;
    });
  };

  const handleMinPayChange = (value: string) => {
    setMinPayInput(value.replace(/[^\d]/g, ''));
  };

  const handleMaxPayChange = (value: string) => {
    setMaxPayInput(value.replace(/[^\d]/g, ''));
  };

  const handleMinPayBlur = () => {
    trackFilterChange('minPay', normalisePayInput(minPayInput));
  };

  const handleMaxPayBlur = () => {
    trackFilterChange('maxPay', normalisePayInput(maxPayInput));
  };

  const handleCadenceSelect = (value: NotificationCadence) => {
    setCadence(value);
    setDefaultCadence(value);
    posthog.capture('jobs_notifications_cadence_selected', { cadence: value });
  };

  const handleSavedSearchCadenceChange = (id: string, value: NotificationCadence) => {
    updateCadence(id, value);
    posthog.capture('jobs_saved_search_cadence_updated', { searchId: id, cadence: value });
  };

  const handleRemoveSavedSearch = (id: string) => {
    removeSearch(id);
    posthog.capture('jobs_saved_search_removed', { searchId: id });
  };

  const minPay = normalisePayInput(minPayInput);
  const maxPay = normalisePayInput(maxPayInput);

  const filters = {
    query: query.trim(),
    location: location.trim(),
    jobType: selectedType,
    returnerFriendly,
    remoteOnly,
    sort,
    tags: selectedTags,
    minPay,
    maxPay,
    occupationId: occupationSearch.data?.[0]?.id,
    page: 1,
    pageSize: 20,
  };
  const { data: jobsData = [], isPending, isError, refetch } = useJobsQuery(filters);

  const canonicalUrl = useMemo(() => {
    if (siteUrl) return `${siteUrl}/jobs`;
    if (typeof window !== 'undefined') {
      return `${window.location.origin}/jobs`;
    }
    return 'https://jobs.lighthouse.careers/jobs';
  }, [siteUrl]);

  const jobListJsonLd = useMemo(() => {
    const baseUrl = canonicalUrl.replace(/\/jobs$/, '');
    const items = jobsData.slice(0, 10).map((job, index) => {
      const detailPath = (job as { slug?: string }).slug ?? job.id;
      const employerName =
        (job as { company?: string }).company ??
        (job as { employer?: { name?: string } }).employer?.name;
      return {
        '@type': 'ListItem',
        position: index + 1,
        url: `${baseUrl}/jobs/${detailPath}`,
        name: job.title,
        description: job.description?.slice(0, 180),
        employer: employerName,
      };
    });
    return {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: 'Lighthouse job listings',
      itemListElement: items,
    };
  }, [jobsData, canonicalUrl]);

  const handleApplySavedSearch = (search: SavedSearch) => {
    setQuery(search.query);
    setLocation(search.location);
    setSelectedType(search.jobType);
    setReturnerFriendly(search.returnerFriendly);
    setRemoteOnly(search.remoteOnly);
    setSelectedTags(search.tags ?? []);
    setMinPayInput(search.minPay ? String(search.minPay) : '');
    setMaxPayInput(search.maxPay ? String(search.maxPay) : '');
    toast.success(`Applied saved search: ${search.name}`);
    posthog.capture('jobs_saved_search_applied', { searchId: search.id, cadence: search.cadence });
  };

  const handleSaveSearch = () => {
    const defaultName =
      query || location ? `${query || 'Any role'} / ${location || 'Any location'}` : 'New saved search';
    const name = window.prompt('Name your search', defaultName);
    if (!name) return;

    addSearch({
      name: name.trim(),
      query,
      location,
      jobType: selectedType,
      returnerFriendly,
      remoteOnly,
      tags: selectedTags,
      minPay,
      maxPay,
      cadence,
    });
    toast.success('Search saved');
    posthog.capture('jobs_saved_search_created', {
      hasQuery: Boolean(query.trim()),
      hasLocation: Boolean(location.trim()),
      cadence,
      returnerFriendly,
      remoteOnly,
      jobType: selectedType,
    });
  };

  const resetFilters = () => {
    setQuery('');
    setLocation('');
    setSelectedType('all');
    setReturnerFriendly(false);
    setRemoteOnly(false);
    setSort('newest');
    setSelectedTags([]);
    setMinPayInput('');
    setMaxPayInput('');
    posthog.capture('jobs_filters_reset');
  };

  const resultsCount = jobsData.length;
  const describeSavedSearch = (search: SavedSearch) => {
    const base = `${search.query || 'Any role'} · ${search.location || 'Any location'}`;
    const tagsSummary = search.tags.length ? ` · Tags: ${search.tags.join(', ')}` : '';
    const paySummary = buildPaySummary(search.minPay, search.maxPay);
    const paySegment = paySummary ? ` · Pay ${paySummary}` : '';
    return `${base}${tagsSummary}${paySegment}`;
  };
  const renderSavedSearchList = (variant: 'default' | 'sheet' = 'default') =>
    savedSearches.map((search) => (
      <div
        key={search.id}
        className={`rounded-lg border border-white/10 bg-white/[0.03] p-4 text-sm text-gray-200 ${
          variant === 'sheet' ? 'shadow-lg shadow-black/10' : ''
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="font-semibold text-white">{search.name}</p>
            <p className="text-xs text-gray-400">{describeSavedSearch(search)}</p>
            {search.tags.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {search.tags.map((tag) => (
                  <Badge key={`${search.id}-${tag}`} variant="outline" className="border-white/15 text-[0.65rem] text-gray-100">
                    {tag}
                  </Badge>
                ))}
              </div>
            )}
            {buildPaySummary(search.minPay, search.maxPay) && (
              <p className="mt-2 text-xs text-gray-400">Pay preference: {buildPaySummary(search.minPay, search.maxPay)}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="icon"
              variant="ghost"
              className="text-gray-300 hover:text-[#f7ef8a]"
              onClick={() => handleApplySavedSearch(search)}
            >
              <Bookmark className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="text-gray-300 hover:text-[#f7ef8a]"
              onClick={() => shareSavedSearch(search)}
            >
              <Share2 className="h-4 w-4" aria-hidden />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="text-gray-500 hover:text-red-300"
              onClick={() => handleRemoveSavedSearch(search.id)}
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </Button>
          </div>
        </div>
        <div className="mt-3">
          <label className="text-xs uppercase tracking-[0.3em] text-gray-500">Cadence</label>
          <Select
            value={search.cadence}
            onValueChange={(value) => handleSavedSearchCadenceChange(search.id, value as NotificationCadence)}
          >
            <SelectTrigger className="mt-1 w-full border-white/10 bg-white/5 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-[#0d1022] text-white">
              <SelectItem value="daily">Daily</SelectItem>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="off">Off</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    ));
  const savedSearchesCount = savedSearches.length;
  const savedSearchesReady = !savedSearchesLoading && !savedSearchesError;
  const savedSearchesAvailable = savedSearchesReady && savedSearchesCount > 0;
  const highlightedSavedSearch = mostRecent ?? savedSearches.at(0) ?? null;

  return (
    <>
      <Helmet>
        <title>Browse inclusive jobs | Lighthouse</title>
        <meta
          name="description"
          content="Search verified, returner-friendly roles with flexible work options, mentorship, and inclusive hiring."
        />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content="Browse inclusive jobs | Lighthouse" />
        <meta
          property="og:description"
          content="Discover curated opportunities from verified employers committed to inclusive hiring."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jobListJsonLd)}</script>
      </Helmet>
      <main className="min-h-screen bg-[#090b1a] pb-20" aria-labelledby="jobs-hero-heading">
      <header className="border-b border-white/10 bg-[#0d1022]/80 backdrop-blur-sm">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#d2ac47]/30 bg-[#ba9036]/10 px-4 py-1 text-xs font-semibold uppercase tracking-[0.3em] text-[#f7ef8a]">
              Discover roles
            </div>
            <div className="space-y-4">
              <h1 id="jobs-hero-heading" className="text-4xl font-semibold text-white sm:text-5xl">
                Explore open opportunities
              </h1>
              <p className="text-lg text-gray-300 sm:max-w-2xl">
                Search across verified employers, returner programmes, and roles curated by Lighthouse. Use the filters to
                tailor your matches.
              </p>
            </div>
            <section
              aria-label="Job search filters"
              className="grid gap-4 rounded-2xl border border-white/10 bg-white/5 p-6 shadow-lg shadow-[#d2ac47]/10 md:grid-cols-[2fr_2fr_1fr]"
            >
              <div className="space-y-2">
                <label htmlFor="jobs-query" className="text-sm text-gray-300">
                  Search by role or skill
                </label>
                <Input
                  id="jobs-query"
                  placeholder="e.g. Product manager, dementia care, React"
                  value={query}
                  onChange={(event) => handleQueryChange(event.target.value)}
                  onBlur={handleQueryBlur}
                  className="border-white/10 bg-white/10 text-white placeholder:text-gray-400"
                />
                {query.trim().length >= 2 && occupationSearch.data && occupationSearch.data.length > 0 && (
                  <div className="mt-2 space-y-1 rounded-lg border border-white/10 bg-black/60 p-2 text-sm text-gray-200">
                    <p className="text-[11px] uppercase tracking-[0.35em] text-gray-500">Occupation suggestions</p>
                    <div className="flex flex-wrap gap-2">
                      {occupationSearch.data.slice(0, 6).map((occ) => (
                        <Button
                          key={occ.id}
                          type="button"
                          variant="outline"
                          className="border-white/15 bg-white/5 text-xs text-white hover:bg-white/10"
                          onClick={() => {
                            setQuery(occ.preferredLabel);
                            trackFilterChange('query', occ.preferredLabel);
                          }}
                        >
                          {occ.preferredLabel}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <label htmlFor="jobs-location" className="text-sm text-gray-300">
                  Location
                </label>
                <Input
                  id="jobs-location"
                  placeholder="City, region or remote"
                  value={location}
                  onChange={(event) => handleLocationChange(event.target.value)}
                  onBlur={handleLocationBlur}
                  className="border-white/10 bg-white/10 text-white placeholder:text-gray-400"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm text-gray-300" htmlFor="job-type">
                  Job type
                </label>
                <Select value={selectedType} onValueChange={handleJobTypeChange}>
                  <SelectTrigger id="job-type" className="border-white/10 bg-white/10 text-white">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#0d1022] text-white">
                    <SelectItem value="all">All types</SelectItem>
                    {jobTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-wrap items-center gap-3 md:col-span-3">
                <Checkbox
                  id="returner-friendly"
                  checked={returnerFriendly}
                  onCheckedChange={handleReturnerFriendlyChange}
                />
                <label htmlFor="returner-friendly" className="text-sm text-gray-300">
                  Returner friendly programmes only
                </label>
                <Checkbox
                  id="remote-only"
                  checked={remoteOnly}
                  onCheckedChange={handleRemoteOnlyChange}
                  className="ml-4"
                />
                <label htmlFor="remote-only" className="text-sm text-gray-300">
                  Remote first roles
                </label>
                <Select value={sort} onValueChange={(value) => handleSortChange(value as SortValue)}>
                  <SelectTrigger className="ml-auto w-48 border-white/10 bg-white/10 text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-white/10 bg-[#0d1022] text-white">
                    {sortOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button variant="ghost" className="text-sm text-gray-300 hover:text-[#f7ef8a]" onClick={resetFilters}>
                  Reset
                </Button>
              </div>
              <div className="space-y-2 md:col-span-3">
                <label className="text-sm text-gray-300">Focus tags</label>
                <p className="text-xs text-gray-500">Toggle the work styles or programmes you care about most.</p>
                <div className="flex flex-wrap gap-2">
                  {tagOptions.map((tag) => {
                    const active = selectedTags.includes(tag.value);
                    return (
                      <Button
                        key={tag.value}
                        type="button"
                        variant={active ? 'default' : 'outline'}
                        className={`border-white/15 text-xs uppercase tracking-wide ${
                          active ? 'bg-gold-gradient text-black hover:brightness-110' : 'bg-transparent text-white hover:bg-white/10'
                        }`}
                        onClick={() => toggleTag(tag.value)}
                      >
                        {tag.label}
                      </Button>
                    );
                  })}
                </div>
              </div>
              <div className="grid gap-4 md:col-span-3 md:grid-cols-2">
                <div className="space-y-2">
                  <label htmlFor="min-pay" className="text-sm text-gray-300">
                    Minimum salary (GBP)
                  </label>
                  <Input
                    id="min-pay"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="60000"
                    value={minPayInput}
                    onChange={(event) => handleMinPayChange(event.target.value)}
                    onBlur={handleMinPayBlur}
                    className="border-white/10 bg-white/10 text-white placeholder:text-gray-500"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="max-pay" className="text-sm text-gray-300">
                    Maximum salary (GBP)
                  </label>
                  <Input
                    id="max-pay"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    placeholder="120000"
                    value={maxPayInput}
                    onChange={(event) => handleMaxPayChange(event.target.value)}
                    onBlur={handleMaxPayBlur}
                    className="border-white/10 bg-white/10 text-white placeholder:text-gray-500"
                  />
                </div>
              </div>
            </section>

            {savedSearchesLoading && (
              <div className="rounded-2xl border border-white/10 bg-white/5 p-4 text-sm text-gray-300">
                Syncing your saved searches...
              </div>
            )}
            {savedSearchesError && !savedSearchesLoading && (
              <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-100">
                Could not load saved searches. Please refresh and try again.
              </div>
            )}
            {savedSearchesReady && (
              <div className="rounded-2xl border border-white/10 bg-gradient-to-r from-white/[0.05] to-white/[0.02] p-5">
                <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-[0.3em] text-gray-500">Job alerts</p>
                    <p className="text-white">
                      {savedSearchesAvailable
                        ? cadence === 'off'
                          ? 'Alerts are paused for now.'
                          : `Receiving ${cadence === 'daily' ? 'daily' : 'weekly'} matches${
                              highlightedSavedSearch ? ` for '${highlightedSavedSearch.name}'` : ''
                            }.`
                        : 'Save a search to start receiving curated alerts from Lighthouse.'}
                    </p>
                    {savedSearchesAvailable && highlightedSavedSearch && (
                      <p className="text-sm text-gray-400">{describeSavedSearch(highlightedSavedSearch)}</p>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                    <Button
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10"
                      onClick={handleSaveSearch}
                      disabled={savedSearchesLoading}
                    >
                      Save new search
                    </Button>
                    <Button
                      className="bg-gold-gradient text-black hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-60"
                      asChild
                      disabled={!savedSearchesAvailable}
                    >
                      <Link to="/notifications">Manage alerts</Link>
                    </Button>
                  </div>
                </div>
                <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
                  <div className="flex-1">
                    <label className="text-xs uppercase tracking-[0.3em] text-gray-500">Alert cadence</label>
                    <Select
                      value={cadence}
                      onValueChange={(value) => handleCadenceSelect(value as NotificationCadence)}
                      disabled={settingsLoading}
                    >
                      <SelectTrigger className="mt-2 w-full border-white/10 bg-[#0d1022] text-white">
                        <SelectValue placeholder="Choose cadence" />
                      </SelectTrigger>
                      <SelectContent className="border-white/10 bg-[#0d1022] text-white">
                        {cadenceOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="mt-1 text-xs text-gray-400">
                      {cadenceOptions.find((option) => option.value === cadence)?.description}
                    </p>
                  </div>
                  {settingsError && (
                    <p className="text-xs text-red-300">Could not update alert cadence. Please try again.</p>
                  )}
                </div>
              </div>
            )}

            <div className="md:hidden">
              <Sheet open={savedSearchSheetOpen} onOpenChange={setSavedSearchSheetOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    className="w-full border border-white/15 bg-white/5 text-white hover:bg-white/10"
                    disabled={savedSearchesLoading}
                  >
                    {savedSearchesAvailable ? `Saved searches (${savedSearchesCount})` : 'Saved searches'}
                  </Button>
                </SheetTrigger>
                <SheetContent
                  side="bottom"
                  className="flex h-[85vh] flex-col border-white/10 bg-[#040712] text-white sm:max-w-full"
                >
                  <SheetHeader>
                    <SheetTitle>Saved searches</SheetTitle>
                  </SheetHeader>
                  <div className="mt-6 space-y-4 overflow-y-auto pr-1">
                    {savedSearchesLoading && <p className="text-sm text-gray-400">Loading saved searches...</p>}
                    {savedSearchesError && !savedSearchesLoading && (
                      <p className="text-sm text-red-300">Could not load saved searches. Please try again.</p>
                    )}
                    {!savedSearchesLoading && !savedSearchesError && savedSearches.length === 0 && (
                      <p className="text-sm text-gray-400">No saved searches yet. Save this search to get alerts.</p>
                    )}
                    {!savedSearchesLoading && !savedSearchesError && savedSearches.length > 0 && (
                      <div className="space-y-3">{renderSavedSearchList('sheet')}</div>
                    )}
                  </div>
                </SheetContent>
              </Sheet>
            </div>
          </div>
        </div>
      </header>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8" aria-label="Job listings">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-gray-400">
            Showing <span className="font-semibold text-[#f7ef8a]">{resultsCount}</span> live roles
          </p>
          <Link to="/pricing" className="inline-flex items-center gap-2 text-sm text-[#f7ef8a] hover:text-[#f7ef8a]">
            <Sparkles className="h-4 w-4" aria-hidden />
            Upgrade to unlock matching co-pilot
          </Link>
        </div>

        <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,3fr)_minmax(0,1fr)]">
          <div className="space-y-4">
            {isError && (
              <Card className="border-red-500/40 bg-red-500/10 text-sm text-red-100">
                <CardContent className="flex items-center justify-between p-4">
                  <p>We couldn&apos;t fetch fresh jobs. Showing the last cached results.</p>
                  <Button size="sm" variant="outline" className="border-red-400 text-red-200" onClick={() => refetch()}>
                    Try again
                  </Button>
                </CardContent>
              </Card>
            )}

            {isPending ? (
              <JobsSkeleton />
            ) : jobsData.length === 0 ? (
              <Card className="border-white/10 bg-white/[0.02]">
                <CardContent className="p-10 text-center text-sm text-gray-300">
                  No roles match your filters yet. Save this search to get notified when new jobs go live.
                </CardContent>
              </Card>
            ) : (
              jobsData.map((job) => {
                const employerName =
                  (job as { company?: string }).company ??
                  (job as { employer?: { name?: string } }).employer?.name ??
                  'Your organisation';
                const jobSlug = (job as { slug?: string }).slug ?? job.id;
                const verifiedEmployer = isEmployerVerified(job);
                const occupationLabel =
                  (job as { occupation?: { preferredLabel?: string } })?.occupation?.preferredLabel ??
                  (job as { occupationTitle?: string | null })?.occupationTitle ??
                  (job as { occupationId?: string | null })?.occupationId;
                return (
                  <Card key={job.id} className="border-white/10 bg-white/[0.02]">
                    <CardHeader className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                      <div>
                        <div className="flex flex-wrap items-center gap-3">
                          <CardTitle className="text-xl text-white">{job.title}</CardTitle>
                          {job.returnerFriendly && (
                            <Badge className="bg-gold-gradient text-[#121321]">Returner friendly</Badge>
                          )}
                          {verifiedEmployer && (
                            <Badge variant="outline" className="border-[#d2ac47]/40 text-[#f7ef8a]">
                              Verified employer
                            </Badge>
                          )}
                        </div>
                        <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-gray-300">
                          <span className="flex items-center gap-1">
                            <Building2 className="h-4 w-4 text-[#ba9036]" aria-hidden />
                            {employerName}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="h-4 w-4 text-[#ba9036]" aria-hidden />
                            {job.location}
                          </span>
                          <span className="flex items-center gap-1">
                            <Briefcase className="h-4 w-4 text-[#ba9036]" aria-hidden />
                            {job.type}
                          </span>
                          {occupationLabel && (
                            <span className="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2 py-1 text-xs text-gray-200">
                              {occupationLabel}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-start gap-2 rounded-xl border border-white/10 bg-white/[0.02] px-4 py-2 text-sm text-gray-300 md:items-end">
                        <span className="text-xs uppercase tracking-[0.3em] text-gray-500">Salary</span>
                        <span className="text-lg font-semibold text-white">{job.salary || 'Competitive'}</span>
                        <span>{job.posted}</span>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-6 p-6 pt-0 text-sm text-gray-300">
                      <p className="line-clamp-3 leading-6">{job.description}</p>
                      <div className="flex flex-wrap gap-3">
                        {(job.requirements ?? []).slice(0, 3).map((requirement) => (
                          <Badge key={requirement} variant="outline" className="border-white/10 text-gray-200">
                            {requirement}
                          </Badge>
                        ))}
                      </div>
                      <div className="flex flex-wrap gap-3">
                        <Button asChild className="bg-gold-gradient text-black hover:brightness-110">
                          <Link to={`/jobs/${jobSlug}`}>View details</Link>
                        </Button>
                        <Button variant="outline" className="border-white/20 text-white hover:bg-white/10">
                          Save role
                        </Button>
                        <Button variant="ghost" className="text-gray-400 hover:text-[#f7ef8a]">
                          Share
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>

          <aside className="space-y-4" aria-label="Saved searches and returner resources">
            <Card className="border-white/10 bg-white/[0.03]">
              <CardContent className="space-y-4 p-6">
                <h2 className="text-lg font-semibold text-white">Stay updated</h2>
                <p className="text-sm text-gray-300">
                  Save this search to receive weekly matches curated by Lighthouse.
                </p>
                <Button type="button" onClick={handleSaveSearch} className="w-full bg-gold-gradient text-black hover:brightness-110">
                  Save search
                </Button>
              </CardContent>
            </Card>

            <Card className="border-white/10 bg-white/[0.03]">
              <CardHeader className="border-b border-white/5 pb-4">
                <CardTitle className="text-base text-white">Saved searches</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 p-6">
                {savedSearchesLoading && <p className="text-sm text-gray-400">Loading saved searches...</p>}
                {savedSearchesError && !savedSearchesLoading && (
                  <p className="text-sm text-red-300">Could not load saved searches. Please try again.</p>
                )}
                {!savedSearchesLoading && !savedSearchesError && savedSearches.length === 0 && (
                  <p className="text-sm text-gray-400">No saved searches yet.</p>
                )}
                {!savedSearchesLoading && !savedSearchesError && savedSearches.length > 0 && renderSavedSearchList()}
              </CardContent>
            </Card>

          <Card className="border-white/10 bg-white/[0.03]">
            <CardContent className="space-y-4 p-6">
              <h2 className="text-lg font-semibold text-white">Need help returning?</h2>
              <p className="text-sm text-gray-300">
                Explore our Returner Hub for mentorship, training, and flexible roles handpicked for career comebacks.
                </p>
                <Button variant="outline" className="w-full border-[#d2ac47]/40 text-[#f7ef8a] hover:bg-[#ba9036]/10" asChild>
                  <Link to="/returner-hub">Visit Returner Hub</Link>
                </Button>
              </CardContent>
            </Card>
          </aside>
        </div>
      </section>
      </main>
    </>
  );
}

function JobsSkeleton() {
  return (
    <>
      {Array.from({ length: 3 }).map((_, index) => (
        <Card key={`jobs-skeleton-${index}`} className="border-white/10 bg-white/[0.02]">
          <CardContent className="space-y-4 p-6">
            <div className="h-6 w-2/3 animate-pulse rounded bg-white/10" />
            <div className="flex flex-wrap gap-3">
              <div className="h-4 w-24 animate-pulse rounded bg-white/10" />
              <div className="h-4 w-20 animate-pulse rounded bg-white/10" />
              <div className="h-4 w-16 animate-pulse rounded bg-white/10" />
            </div>
            <div className="space-y-2">
              <div className="h-3 w-full animate-pulse rounded bg-white/10" />
              <div className="h-3 w-11/12 animate-pulse rounded bg-white/10" />
              <div className="h-3 w-10/12 animate-pulse rounded bg-white/10" />
            </div>
            <div className="flex gap-3">
              <div className="h-10 w-32 animate-pulse rounded bg-white/10" />
              <div className="h-10 w-24 animate-pulse rounded bg-white/10" />
              <div className="h-10 w-24 animate-pulse rounded bg-white/10" />
            </div>
          </CardContent>
        </Card>
      ))}
    </>
  );
}

